import { Injectable } from '@angular/core';
import { AuthService } from '@core/auth/auth.service';
import { PerdoruesiStatusi } from '@core/enums/perdoruesi-statusi.enum';
import { BazaKomenti } from '@core/interfaces/models/baza-komenti.interface';
import { Kerkimi } from '@core/interfaces/models/kerkimi.interface';
import { Njoftimi } from '@core/interfaces/models/njoftimi.interface';
import { PerdoruesiConnection } from '@core/interfaces/signalr/perdoruesi-connection.interface';
import { Perdoruesi } from '@core/interfaces/signalr/perdoruesi.interface';
import * as signalR from '@microsoft/signalr';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { BehaviorSubject, combineLatest, map, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MorfologjiaHubService {
  private _connectionEstablished: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _perdoruesit$: BehaviorSubject<Perdoruesi[]> = new BehaviorSubject<Perdoruesi[]>([]);
  private _connectionId$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  private _hubConnection: HubConnection | undefined;

  constructor(private authService: AuthService) {}

  getConnectionId() {
    return this._connectionId$.asObservable();
  }

  getPerdoruesit() {
    return this._perdoruesit$.asObservable();
  }

  statusiUpdate(statusi: PerdoruesiStatusi) {
    this._hubConnection?.invoke('StatusiUpdate', statusi);
  }

  getConnectionEstablished() {
    return this._connectionEstablished.asObservable();
  }

  //#region Baza

  private _bazaInserted$: Subject<Kerkimi[]> = new Subject();
  private _bazaUpdated$: Subject<Kerkimi[]> = new Subject();
  private _bazaDeleted$: Subject<number[]> = new Subject();
  private _bazaKomentiInserted$: Subject<BazaKomenti> = new Subject();
  private _bazaKomentiUpdated$: Subject<BazaKomenti> = new Subject();
  private _bazaKomentiDeleted$: Subject<BazaKomenti> = new Subject();

  getLockedBazat() {
    return combineLatest([this._perdoruesit$, this._connectionId$]).pipe(
      map(([perdoruesit, connectionId]) => {
        return perdoruesit.map((p) => {
          return { operatoriName: p.operatoriName, bazaIds: p.connections.filter((c) => c?.connectionId != connectionId)?.flatMap((c) => c?.bazaIds) };
        });
      })
    );
  }

  addLockedBazaId(id: number) {
    this._hubConnection?.invoke('AddBazaId', id);
  }

  removeLockedBazaId(id: number) {
    this._hubConnection?.invoke('RemoveBazaId', id);
  }

  setLockedBazaIds(ids: number[]) {
    this._hubConnection?.invoke('SetBazaIds', ids);
  }

  onBazaInserted() {
    return this._bazaInserted$.asObservable();
  }

  onBazaUpdated() {
    return this._bazaUpdated$.asObservable();
  }

  onBazaDeleted() {
    return this._bazaDeleted$.asObservable();
  }

  onBazaKomentiInserted() {
    return this._bazaKomentiInserted$.asObservable();
  }

  onBazaKomentiUpdated() {
    return this._bazaKomentiUpdated$.asObservable();
  }

  onBazaKomentiDeleted() {
    return this._bazaKomentiDeleted$.asObservable();
  }

  //#endregion

  private _notificationReceived$: Subject<Njoftimi> = new Subject();

  onNotificationReceived() {
    return this._notificationReceived$.asObservable();
  }

  initializeSignalR() {
    this.createConnection();
    return this.startConnection();
  }

  async closeConnection() {
    await this._hubConnection?.stop();
    this._perdoruesit$.next([]);
    this._connectionEstablished.next(false);
    return true;
  }

  private createConnection() {
    this._hubConnection = new HubConnectionBuilder()
      .withUrl(environment.signalRUrl, {
        accessTokenFactory: () => {
          return this.authService.accessToken;
        },
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .configureLogging(environment.production ? LogLevel.None : LogLevel.Error)
      .withAutomaticReconnect()
      .build();

    this._hubConnection.serverTimeoutInMilliseconds = 5000000;

    this._hubConnection?.onclose(() => {
      this._connectionEstablished.next(false);
      this._perdoruesit$.next([]);
    });
    this._hubConnection?.onreconnected(() => {
      this._connectionEstablished.next(true);
    });
  }

  private async startConnection() {
    try {
      await this._hubConnection?.start().then(() => {
        this._hubConnection?.invoke('OnClientConnected');
        this._connectionEstablished.next(true);
        this.registerOnServerEvents();
      });
      // this._hubConnection?.invoke('OnClientConnected');
      return true;
    } catch (err) {
      console.log('Error while establishing connection, retrying...');
      this._connectionEstablished.next(false);
      this._perdoruesit$.next([]);
      setTimeout(() => {
        this.startConnection();
      }, 5000);
      return false;
    }
  }

  private registerOnServerEvents(): void {
    this._hubConnection?.on('OnConnect', (connectionId: string, perdoruesit: Perdoruesi[]) => {
      this._connectionId$.next(connectionId);
      this._perdoruesit$.next(perdoruesit);
      this._connectionEstablished.next(true);
    });
    this._hubConnection?.on('PerdoruesiConnected', (perdoruesi: Perdoruesi) => {
      const perdoruesit: Perdoruesi[] = [...this._perdoruesit$.value];
      perdoruesit.push(perdoruesi);
      this._perdoruesit$.next(perdoruesit);
    });
    this._hubConnection?.on('PerdoruesiNewConnection', (operatoriId: string, connection: PerdoruesiConnection) => {
      const perdoruesit: Perdoruesi[] = [...this._perdoruesit$.value];
      if (perdoruesit.length > 0) {
        const index = perdoruesit.findIndex((p) => p.operatoriId == operatoriId);
        perdoruesit[index].connections.push(connection);
        this._perdoruesit$.next(perdoruesit);
      }
    });
    this._hubConnection?.on('PerdoruesiConnectionUpdated', (connection: PerdoruesiConnection) => {
      const perdoruesit: Perdoruesi[] = [...this._perdoruesit$.value];
      if (perdoruesit.length > 0) {
        const index = perdoruesit.findIndex((p) => p.connections.some((c) => c.connectionId == connection.connectionId));
        const connectionIndex = perdoruesit[index].connections.findIndex((c) => c.connectionId == connection.connectionId);
        perdoruesit[index].connections[connectionIndex] = connection;
        this._perdoruesit$.next(perdoruesit);
      }
    });
    this._hubConnection?.on('PerdoruesiConnectionsUpdated', (connections: PerdoruesiConnection[]) => {
      const perdoruesit: Perdoruesi[] = [...this._perdoruesit$.value];
      if (perdoruesit.length > 0) {
        connections.forEach((connection) => {
          const index = perdoruesit.findIndex((p) => p.connections.some((c) => c.connectionId == connection.connectionId));
          const connectionIndex = perdoruesit[index].connections.findIndex((c) => c.connectionId == connection.connectionId);
          perdoruesit[index].connections[connectionIndex] = connection;
        });
        this._perdoruesit$.next(perdoruesit);
      }
    });
    this._hubConnection?.on('PerdoruesiDroppedConnection', (connectionId: string) => {
      const perdoruesit: Perdoruesi[] = [...this._perdoruesit$.value];
      const index = perdoruesit.findIndex((p) => p.connections.some((c) => c.connectionId == connectionId));
      const connectionIndex = perdoruesit[index].connections.findIndex((c) => c.connectionId == connectionId);
      perdoruesit[index].connections.splice(connectionIndex, 1);
      this._perdoruesit$.next(perdoruesit);
    });
    this._hubConnection?.on('PerdoruesiDisconnected', (perdoruesi: Perdoruesi) => {
      const perdoruesit: Perdoruesi[] = [...this._perdoruesit$.value];
      const index = perdoruesit.findIndex((p) => p.operatoriId == perdoruesi.operatoriId);
      perdoruesit.splice(index, 1);
      this._perdoruesit$.next(perdoruesit);
    });
    this._hubConnection?.on('NotificationReceived', (njoftimi: Njoftimi) => {
      this._notificationReceived$.next(njoftimi);
    });

    this.registerBazaServerEvents();
  }

  registerBazaServerEvents() {
    this._hubConnection?.on('OnBazaInserted', (kerkimet: Kerkimi[]) => {
      this._bazaInserted$.next(kerkimet);
    });
    this._hubConnection?.on('OnBazaUpdated', (kerkimet: Kerkimi[]) => {
      this._bazaUpdated$.next(kerkimet);
    });
    this._hubConnection?.on('OnBazaDeleted', (bazaIds: number[]) => {
      this._bazaDeleted$.next(bazaIds);
    });
    this._hubConnection?.on('OnBazaKomentiInserted', (komenti: BazaKomenti) => {
      this._bazaKomentiInserted$.next(komenti);
    });
    this._hubConnection?.on('OnBazaKomentiUpdated', (komenti: BazaKomenti) => {
      this._bazaKomentiUpdated$.next(komenti);
    });
    this._hubConnection?.on('OnBazaKomentiDeleted', (komenti: BazaKomenti) => {
      this._bazaKomentiDeleted$.next(komenti);
    });
  }

  unregisterBazaServerEvents() {
    this._hubConnection?.off('OnBazaInserted');
    this._hubConnection?.off('OnBazaUpdated');
    this._hubConnection?.off('OnBazaDeleted');
    this._hubConnection?.off('OnKerkimiRadhitjaUpdated');
    this._hubConnection?.off('OnBazaKomentiInserted');
    this._hubConnection?.off('OnBazaKomentiUpdated');
    this._hubConnection?.off('OnBazaKomentiDeleted');
  }
  
}
